import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import type { LookInsidePage } from '../data/lookInsidePages';
type PageLightboxProps = {
  pages: LookInsidePage[];
  index: number | null;
  onClose: () => void;
  onChange: (index: number) => void;
};
const ease = [0.23, 1, 0.32, 1] as const;
export function PageLightbox({
  pages,
  index,
  onClose,
  onChange
}: PageLightboxProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const open = index !== null;
  const prev = () => {
    if (index === null) return;
    onChange((index - 1 + pages.length) % pages.length);
  };
  const next = () => {
    if (index === null) return;
    onChange((index + 1) % pages.length);
  };
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index]);
  const page = index !== null ? pages[index] : null;
  const navBtn = 'absolute top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white transition-colors duration-150 hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal';
  return <AnimatePresence data-id="element-199" data-mp="va0v7oezg59z">
      {open && page && <motion.div role="dialog" aria-modal="true" aria-label={page.alt} className="fixed inset-0 z-[60] flex items-center justify-center bg-navy-deep/85 px-4 py-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} onClick={onClose} data-id="element-200" data-mp="vasfw471kilh">
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white transition-colors duration-150 hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal" data-id="element-201" data-mp="114r04oaopf37">
            <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-202" data-mp="i7gc5zui6832" />
          </button>

          <button type="button" onClick={e => {
        e.stopPropagation();
        prev();
      }} aria-label="Previous page" className={`${navBtn} left-3 sm:left-6`} data-id="element-203" data-mp="w66a1hp5cbsh">
            <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-204" data-mp="s6fe6zkrj5h3" />
          </button>

          <motion.figure key={page.src} className="flex max-h-full flex-col items-center" initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.96
      }} transition={{
        duration: 0.22,
        ease
      }} onClick={e => e.stopPropagation()} data-id="element-205" data-mp="27j9vxm9xeywl">
            <img src={page.src} alt={page.alt} width={1080} height={1920} className="max-h-[82vh] w-auto max-w-[80vw] rounded-xl bg-white object-contain shadow-cover" data-id="element-206" data-mp="215w2wx7pnf8c" />
            <figcaption className="mt-3 text-center text-sm text-white/85" data-id="element-207" data-mp="2yslf1zw4n87x">
              {page.caption}
              <span className="ml-2 text-white/55" data-id="element-208" data-mp="2k3owva0cwetl">
                {index! + 1} / {pages.length}
              </span>
            </figcaption>
          </motion.figure>

          <button type="button" onClick={e => {
        e.stopPropagation();
        next();
      }} aria-label="Next page" className={`${navBtn} right-3 sm:right-6`} data-id="element-209" data-mp="2p0yxzmx3yex9">
            <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-210" data-mp="3hjmdmmtc51qy" />
          </button>
        </motion.div>}
    </AnimatePresence>;
}